import { useEffect, useMemo, useRef } from 'react';
import { Alert, Paper, Typography } from '@mui/material';
import { Citation, ReviewField } from 'api';
import { buildSegments, computeHighlightRanges, HighlightRange } from '../highlight';

type SourceTextPaneProps = {
  documentText: string | null;
  ocrError: string | null;
  citations: Citation[];
  activeField: ReviewField | null;
  /** True while a field is armed for "Select text reference". */
  selecting: boolean;
  /** The active field's reviewer-selected references. */
  overrideRanges: HighlightRange[];
  onSelectText: (start: number, stop: number, selectedText: string) => void;
};

export const SourceTextPane = ({
  documentText,
  ocrError,
  citations,
  activeField,
  selecting,
  overrideRanges,
  onSelectText,
}: SourceTextPaneProps) => {
  const firstHighlightRef = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const citationsById = useMemo(
    () => new Map(citations.map(c => [c.citationId, c])),
    [citations]
  );

  const ranges = useMemo(() => {
    if (!documentText || !activeField) return [];
    return computeHighlightRanges(
      activeField.citationIds,
      citationsById,
      documentText.length
    );
  }, [documentText, activeField, citationsById]);

  useEffect(() => {
    // Centered, so there's context on both sides.
    firstHighlightRef.current?.scrollIntoView({
      block: 'center',
      behavior: 'smooth',
    });
  }, [ranges]);

  // Turns the browser's selection into offsets into `documentText`. This works
  // because the segments render the text verbatim, so the container's text is
  // exactly `documentText`.
  const handleMouseUp = () => {
    if (!selecting || !documentText || !containerRef.current) return;
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    if (!containerRef.current.contains(range.commonAncestorContainer)) return;

    const preRange = document.createRange();
    preRange.selectNodeContents(containerRef.current);
    preRange.setEnd(range.startContainer, range.startOffset);
    const start = preRange.toString().length;
    const stop = start + range.toString().length;

    if (stop > start) {
      onSelectText(start, stop, documentText.slice(start, stop));
    }
    selection.removeAllRanges();
  };

  if (documentText == null || ocrError != null) {
    return (
      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
        <Typography color="text.secondary">
          {ocrError
            ? `OCR failed: ${ocrError}`
            : 'Source text is unavailable for this document.'}
        </Typography>
      </Paper>
    );
  }

  const segments = buildSegments(documentText, ranges, overrideRanges);
  let firstAssigned = false;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      {selecting && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Highlight the text in the document below that supports this
          field's value.
        </Alert>
      )}
      <Typography
        ref={containerRef}
        variant="body2"
        component="div"
        onMouseUp={handleMouseUp}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
          cursor: selecting ? 'text' : undefined,
        }}
      >
        {segments.map((seg, i) => {
          if (!seg.llm && !seg.override) return <span key={i}>{seg.text}</span>;
          const isFirst = seg.llm && !firstAssigned;
          firstAssigned = firstAssigned || seg.llm;
          return (
            <mark
              key={i}
              ref={isFirst ? firstHighlightRef : undefined}
              style={{
                backgroundColor: seg.override ? '#90caf9' : '#fff59d',
              }}
            >
              {seg.text}
            </mark>
          );
        })}
      </Typography>
    </Paper>
  );
};
